import React, { useEffect, useState, useMemo, Suspense, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { edgeFunctionHeaders } from '@/lib/edgeFunctionHeaders';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin,
  Download,
  Share2,
  Check,
  Loader2,
  Star,
  FileText,
  Calendar,
  Car,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { SEOHead } from '@/components/SEOHead';
import { SHARED_PAGE_SHELL_SLUG, sharedTripDescription, sharedTripTitle } from '@/lib/sharedPage';
import { calculateMonthData, DayData, getVisibilityRating } from '@/utils/calendarData';
import { getShootingQualityScore, getDarknessWindowMinutes, getQualityLabel } from '@/utils/shootingQualityScore';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { useRoutesBetweenStops, type RouteSegment } from '@/hooks/useRoutesBetweenStops';
import { formatDriveDuration, formatDriveDistance } from '@/utils/routeFormatting';
import { VisibilityBars } from '@/components/calendar/VisibilityBars';
import { PackingList } from '@/components/dashboard/PackingList';
import { Backpack, ChevronDown, ChevronUp } from 'lucide-react';

const TripOverviewMap = lazy(() => import('@/components/dashboard/TripOverviewMap').then(m => ({ default: m.TripOverviewMap })));

interface SharedTripData {
  trip: {
    id: string;
    slug: string;
    name: string;
    notes: string | null;
    view_count: number;
    created_at: string;
    updated_at: string;
  };
  stops: Array<{
    id: string;
    location_name: string;
    latitude: number;
    longitude: number;
    arrival_date: string;
    departure_date: string;
    stop_order: number;
    notes: string | null;
  }>;
}

function getMoonPhaseName(phase: number): string {
  const percent = Math.round(phase * 100);
  if (percent <= 5) return 'New Moon';
  if (percent <= 25) return 'Crescent';
  if (percent <= 45) return 'Quarter';
  if (percent <= 75) return 'Gibbous';
  return 'Full Moon';
}

const SharedTrip: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<SharedTripData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showPackingList, setShowPackingList] = useState(false);

  // Fetch trip data
  useEffect(() => {
    if (!slug) return;

    const fetchTrip = async () => {
      const { data: result, error } = await supabase.rpc('get_shared_trip_by_slug', {
        trip_slug: slug,
      });

      if (error || !result?.trip) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setData(result as SharedTripData);
      setLoading(false);

      // Increment view count
      await supabase.rpc('increment_trip_view', { trip_slug: slug });
    };

    fetchTrip();
  }, [slug]);

  // Routes between stops
  const { routes, loading: routesLoading } = useRoutesBetweenStops(data?.stops || []);

  // Calculate night data per stop
  const stopsWithNights = useMemo(() => {
    if (!data?.stops) return [];

    return data.stops.map(stop => {
      const arrival = parseISO(stop.arrival_date);
      const departure = parseISO(stop.departure_date);
      const lastNight = subDays(departure, 1);
      const nights = arrival <= lastNight ? eachDayOfInterval({ start: arrival, end: lastNight }) : [];
      const location = { name: stop.location_name, latitude: stop.latitude, longitude: stop.longitude };

      const nightData = nights.map(night => {
        const monthData = calculateMonthData(location, night.getMonth(), night.getFullYear());
        const dayData = monthData.find(d => d.date === night.getDate());
        return {
          ...dayData,
          displayDate: format(night, 'MMM d, yyyy'),
        };
      }).filter(d => d.moonPhase !== undefined);

      const ratings = nightData.map(n => getVisibilityRating(n as DayData));
      const bestRating = ratings.length > 0 ? Math.max(...ratings) : 0;
      const bestNightIdx = ratings.indexOf(bestRating);
      const goodNights = ratings.filter(r => r >= 4).length;

      return {
        ...stop,
        nightData,
        ratings,
        bestRating,
        bestNight: bestNightIdx >= 0 ? nightData[bestNightIdx] : null,
        totalNights: nightData.length,
        goodNights,
      };
    });
  }, [data]);

  // Date range
  const dateRange = useMemo(() => {
    if (!data?.stops?.length) return '';
    const sorted = [...data.stops].sort((a, b) => a.stop_order - b.stop_order);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return `${format(parseISO(first.arrival_date), 'MMM d')} - ${format(parseISO(last.departure_date), 'MMM d, yyyy')}`;
  }, [data]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* The build prerenders this state as the shell middleware.ts serves
            for every shared trip (ERI-2783). The shell itself is not a page to
            index; Head renders nothing in the body, so hydration still matches. */}
        {slug === SHARED_PAGE_SHELL_SLUG && (
          <SEOHead
            title="Shared Milky Way Trip | Milky Way Planner"
            description="A shared Milky Way photography trip from Milky Way Planner, with stops, dates, and nightly galactic core visibility along the route."
            noindex
          />
        )}
        <h1 className="text-3xl font-bold">Milky Way Trip</h1>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[350px] w-full rounded-xl" />
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 space-y-8">
        <SEOHead title="Trip Not Found" description="This shared trip is no longer available. Plan your own Milky Way photography trip." noindex />
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-semibold">This Trip is No Longer Available</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            The owner may have made this trip private. But you can plan your own
            astrophotography trip with Milky Way Planner.
          </p>
        </div>

        <div className="rounded-xl border border-gold/20 bg-gradient-to-br from-gold/5 to-gold/10 p-8 sm:p-10 space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold tracking-[-0.02em]">Plan Your Own Milky Way Trip</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Find the best nights for Milky Way photography at any location on Earth.
              Our free planner shows you exactly when the galactic core is visible and how much
              the moon will interfere.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto text-sm">
            <div className="space-y-2">
              <p className="font-medium">Free features:</p>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> Monthly visibility calendars</li>
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> 400+ dark sky locations</li>
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> Annual heatmap calendar</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-medium">Premium adds:</p>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> Multi-stop trip planner</li>
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> Weather forecasts & routes</li>
                <li className="flex items-center gap-2"><span className="text-gold">✦</span> Moon planner & exports</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Link to="/features">
              <Button variant="premium" size="lg" className="gap-2 px-8 text-base">
                Get Premium - $25/year
              </Button>
            </Link>
            <Link to="/planner">
              <Button variant="outline" size="lg" className="gap-2">
                Try the Free Planner
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { trip, stops } = data;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <SEOHead
        title={sharedTripTitle(trip.name)}
        description={sharedTripDescription(stops.length, dateRange)}
      />

      {/* Premium upsell banner — top of page */}
      <div className="rounded-xl border border-gold/20 bg-gradient-to-r from-gold/5 to-gold/10 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This trip was shared with you from <span className="font-medium text-gold">Milky Way Planner</span>
            </p>
            <h2 className="text-xl font-bold tracking-[-0.02em]">
              Plan your own Milky Way trips
            </h2>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><span className="text-gold">✦</span> Multi-stop trip itineraries with per-night visibility</li>
              <li className="flex items-center gap-2"><span className="text-gold">✦</span> Weather forecasts and driving routes</li>
              <li className="flex items-center gap-2"><span className="text-gold">✦</span> Moon planner for lunar photography</li>
              <li className="flex items-center gap-2"><span className="text-gold">✦</span> PDF, Excel, and calendar exports</li>
            </ul>
          </div>
          <div className="shrink-0 flex flex-col items-center space-y-2">
            <div className="text-center">
              <span className="text-3xl font-bold font-serif">$25</span>
              <span className="text-muted-foreground ml-1">/year</span>
            </div>
            <Link to="/features">
              <Button variant="premium" size="lg" className="gap-2 px-8">
                Get Premium
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground">That's just $2.08/month</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">{trip.name}</h1>
        <p className="text-muted-foreground">
          {stops.length} stop{stops.length !== 1 ? 's' : ''} · {dateRange}
        </p>
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={generatingPdf}
            onClick={async () => {
              setGeneratingPdf(true);
              try {
                const routeData = routes.map(r => {
                  const fromStop = stops.find(s => s.id === r.fromStopId);
                  const toStop = stops.find(s => s.id === r.toStopId);
                  return {
                    from_order: fromStop?.stop_order ?? 0,
                    to_order: toStop?.stop_order ?? 0,
                    duration: r.duration,
                    distance: r.distance,
                  };
                });
                const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-trip-pdf`, {
                  method: 'POST',
                  // The viewer's login, when there is one, adds the Premium lines (ERI-2597).
                  headers: await edgeFunctionHeaders(),
                  // The function loads this public trip by its slug (ERI-2381).
                  body: JSON.stringify({ slug, units: 'imperial', routes: routeData }),
                });
                if (!response.ok) throw new Error('Failed');
                const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${trip.name.replace(/[^a-zA-Z0-9]/g, '_')}_Trip.pdf`;
                link.click();
                URL.revokeObjectURL(url);
                toast.success('PDF downloaded!');
              } catch {
                toast.error('Failed to generate PDF');
              } finally {
                setGeneratingPdf(false);
              }
            }}
            className="gap-2"
          >
            {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {generatingPdf ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Map */}
      {stops.length > 0 && (
        <Suspense fallback={<div className="h-[350px] rounded-xl bg-muted animate-pulse" />}>
          <TripOverviewMap
            stops={stopsWithNights.map(s => ({
              ...s,
              latitude: s.latitude,
              longitude: s.longitude,
              location_name: s.location_name,
              stop_order: s.stop_order,
            }))}
            routeGeometries={routes.map(r => {
              const fromStop = stops.find(s => s.id === r.fromStopId);
              const toStop = stops.find(s => s.id === r.toStopId);
              return {
                fromStopOrder: fromStop?.stop_order ?? 0,
                toStopOrder: toStop?.stop_order ?? 0,
                coordinates: r.geometry.coordinates.map(
                  ([lng, lat]) => [lat, lng] as [number, number]
                ),
              };
            })}
          />
        </Suspense>
      )}

      {/* Stops */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Itinerary</h2>
        {stopsWithNights
          .sort((a, b) => a.stop_order - b.stop_order)
          .map((stop, idx, arr) => {
            const prevStop = idx > 0 ? arr[idx - 1] : null;
            const routeSegment = prevStop
              ? routes.find(r => r.fromStopId === prevStop.id && r.toStopId === stop.id)
              : null;

            return (
              <React.Fragment key={stop.id}>
                {routeSegment && (
                  <div className="flex items-center gap-1.5 pl-4 py-1 text-xs text-muted-foreground">
                    <Car className="w-3.5 h-3.5 shrink-0" />
                    <span>{formatDriveDuration(routeSegment.duration)}</span>
                    <span className="text-border">·</span>
                    <span>{formatDriveDistance(routeSegment.distance, 'imperial')}</span>
                  </div>
                )}
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                            {idx + 1}
                          </div>
                          <h3 className="font-semibold">{stop.location_name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground pl-8">
                          {format(parseISO(stop.arrival_date), 'MMM d')} - {format(parseISO(stop.departure_date), 'MMM d, yyyy')} · {stop.totalNights} night{stop.totalNights !== 1 ? 's' : ''}
                        </p>
                        {stop.bestNight && stop.bestRating >= 4 && (
                          <p className="text-sm pl-8">
                            <Star className="w-3.5 h-3.5 inline text-primary fill-primary mr-1" />
                            Best night: {stop.bestNight.displayDate} · MW {stop.bestRating}/10
                          </p>
                        )}
                        {stop.notes && (
                          <p className="text-sm text-muted-foreground pl-8 italic">{stop.notes}</p>
                        )}
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <p>{stop.latitude.toFixed(2)}°, {stop.longitude.toFixed(2)}°</p>
                        <p>{stop.goodNights} good night{stop.goodNights !== 1 ? 's' : ''}</p>
                      </div>
                    </div>

                    {/* Per-night table — full detail matching premium view */}
                    {stop.nightData.length > 0 && (
                      <div className="mt-3 pl-8 overflow-x-auto">
                        <table className="text-xs w-full">
                          <thead>
                            <tr className="text-muted-foreground border-b">
                              <th className="text-left py-1 pr-2">Date</th>
                              <th className="text-left py-1 pr-2">Sunrise</th>
                              <th className="text-left py-1 pr-2">Sunset</th>
                              <th className="text-left py-1 pr-2">Core Start</th>
                              <th className="text-left py-1 pr-2">Core End</th>
                              <th className="text-left py-1 pr-2 hidden sm:table-cell">Moonrise</th>
                              <th className="text-left py-1 pr-2 hidden sm:table-cell">Moonset</th>
                              <th className="text-left py-1 pr-2">Moon</th>
                              <th className="text-left py-1 pr-2">Phase</th>
                              <th className="text-left py-1">Visibility</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stop.nightData.map((night: (typeof stop.nightData)[number], nIdx: number) => {
                              const rating = stop.ratings[nIdx];
                              return (
                                <tr key={nIdx} className="border-b border-border/50">
                                  <td className="py-1 pr-2">{night.displayDate}</td>
                                  <td className="py-1 pr-2">{night.sunrise || '--'}</td>
                                  <td className="py-1 pr-2">{night.sunset || '--'}</td>
                                  <td className="py-1 pr-2">{night.galacticCoreStart || '--'}</td>
                                  <td className="py-1 pr-2">{night.galacticCoreEnd || '--'}</td>
                                  <td className="py-1 pr-2 hidden sm:table-cell">{night.moonrise || '--'}</td>
                                  <td className="py-1 pr-2 hidden sm:table-cell">{night.moonset || '--'}</td>
                                  <td className="py-1 pr-2">
                                    {night.moonPhase != null ? `${Math.round(night.moonPhase * 100)}%` : '--'}
                                  </td>
                                  <td className="py-1 pr-2">
                                    {night.moonPhase != null ? getMoonPhaseName(night.moonPhase) : '--'}
                                  </td>
                                  <td className="py-1">
                                    <VisibilityBars rating={rating} size="sm" />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </React.Fragment>
            );
          })}
      </div>

      {/* Packing List — collapsible */}
      {stops.length > 0 && (
        <Card>
          <CardContent className="py-0">
            <button
              onClick={() => setShowPackingList(!showPackingList)}
              className="w-full flex items-center justify-between py-4 text-left"
            >
              <div className="flex items-center gap-2">
                <Backpack className="w-5 h-5 text-muted-foreground" />
                <span className="font-semibold">Suggested Packing List</span>
              </div>
              {showPackingList ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            {showPackingList && (
              <div className="pb-4">
                <PackingList stops={stops} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bottom CTA */}
      <div className="rounded-xl border border-gold/20 bg-gradient-to-br from-gold/5 via-gold/10 to-gold/5 p-8 sm:p-10 text-center space-y-5">
        <h2 className="text-2xl font-bold tracking-[-0.02em]">Create Your Own Milky Way Trip</h2>
        <p className="text-muted-foreground max-w-lg mx-auto leading-relaxed">
          Build multi-stop astrophotography itineraries with per-night visibility ratings,
          real-time weather forecasts, driving routes, and shareable links.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link to="/features">
            <Button variant="premium" size="lg" className="gap-2 px-8 text-base">
              Get Premium - $25/year
            </Button>
          </Link>
          <Link to="/planner">
            <Button variant="outline" size="lg" className="gap-2">
              Try the Free Planner
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer attribution */}
      <p className="text-center text-xs text-muted-foreground pb-8">
        Created with <Link to="/" className="text-primary hover:underline">Milky Way Planner</Link>
      </p>
    </div>
  );
};

export default SharedTrip;
